import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { formatUSD } from '../shared/format';

/**
 * Module 5 — A5.7 "Generate today's actions".
 *
 * Pixel-faithful to the mockup (module5 A5.7): a bottom sheet that lists the
 * actions AUTO-GENERATED from the manager's pay-out gaps — one per bonus
 * component that still has unrealised upside — each with a checkbox, a short
 * coaching line, and the $ upside it unlocks. A running "total upside if all N
 * done" footer + "Save all" bulk-inserts the checked actions into the Action
 * Log (Module 6, source = bonus_gap).
 *
 * Props:
 *   - open, onClose
 *   - gapUsd:    biggest pay-out gap in USD (banner)
 *   - gapComponentLabel: localized component name for the banner
 *   - actions:   [{ key, component, title, description, upsideUsd }]
 *   - onSaveAll: (selectedActions[]) => Promise — caller does the insert
 */
const GenerateActionsSheet = ({ open, onClose, gapUsd, gapComponentLabel, actions = [], onSaveAll }) => {
  const { t } = useTranslation();
  // Default: every generated action is checked (mockup shows 3/4 ticked; we
  // start all-on so "Save all" matches the headline upside).
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setChecked(Object.fromEntries(actions.map((a) => [a.key, true])));
      setError(null);
    }
  }, [open, actions]);

  if (!open) return null;

  const selected = actions.filter((a) => checked[a.key]);
  const totalUpside = selected.reduce((s, a) => s + (a.upsideUsd || 0), 0);

  const toggle = (key) => setChecked((c) => ({ ...c, [key]: !c[key] }));

  const handleSave = async () => {
    if (selected.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await onSaveAll?.(selected);
      onClose?.();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520, background: TCN.surface1,
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          border: `1px solid ${TCN.border}`,
          padding: '16px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)',
          maxHeight: '88dvh', overflowY: 'auto',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header — close · title · Save all */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <button
            type="button" onClick={onClose} aria-label={t('tcn.common.close', 'Close')}
            style={{ background: 'transparent', border: 'none', color: TCN.textDim, cursor: 'pointer', padding: 2 }}
          >
            <Icon name="close" size={22} />
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || selected.length === 0}
            style={{
              background: 'transparent', border: 'none',
              color: selected.length === 0 ? TCN.textMute : TCN.cyan,
              fontSize: 15, fontWeight: 700, cursor: selected.length === 0 ? 'default' : 'pointer',
              fontFamily: TCN.fontSans, padding: 2,
            }}
          >
            {saving ? t('tcn.common.saving', 'Saving…') : t('tcn.m5.generate.saveAll', 'Save all')}
          </button>
        </div>

        <div style={{ fontSize: 26, fontWeight: 700, color: TCN.text, letterSpacing: '-0.02em' }}>
          {t('tcn.m5.generate.title', "Today's actions")}
        </div>
        <div style={{ fontSize: 13, color: TCN.textDim, marginTop: 2, marginBottom: 14 }}>
          {t('tcn.m5.generate.subtitle', 'Generated from pay-out gap')}
        </div>

        {/* Auto-generated banner */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
            background: `${TCN.cyan}14`, border: `1px solid ${TCN.cyan}33`,
            borderRadius: 12, marginBottom: 14,
          }}
        >
          <Icon name="sparkle" size={16} color={TCN.cyan} />
          <span style={{ fontSize: 13, color: TCN.text }}>
            {t('tcn.m5.generate.banner', 'Auto-generated from {{amount}} {{component}} gap', {
              amount: formatUSD(gapUsd, { fractionDigits: gapUsd < 100 ? 2 : 0 }),
              component: gapComponentLabel || '',
            })}
          </span>
        </div>

        {/* Action checklist */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {actions.map((a) => {
            const on = !!checked[a.key];
            return (
              <button
                key={a.key}
                type="button"
                onClick={() => toggle(a.key)}
                style={{
                  width: '100%', textAlign: 'start', cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'flex-start', gap: 12, padding: 14,
                  background: TCN.surface2,
                  border: `1px solid ${on ? `${TCN.cyan}55` : TCN.border}`,
                  borderRadius: 14,
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    width: 24, height: 24, borderRadius: 7, flexShrink: 0, marginTop: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: on ? TCN.cyan : 'transparent',
                    border: on ? `1px solid ${TCN.cyan}` : `1px solid ${TCN.borderStrong}`,
                  }}
                >
                  {on && <Icon name="check" size={15} color="#06141a" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: TCN.text }}>{a.title}</div>
                  {a.description && (
                    <div style={{ fontSize: 12, color: TCN.textDim, marginTop: 3, lineHeight: 1.4 }}>
                      {a.description}
                    </div>
                  )}
                </div>
                {a.upsideUsd > 0 && (
                  <span
                    dir="ltr"
                    style={{
                      flexShrink: 0, fontSize: 12, fontWeight: 700, color: TCN.green,
                      background: `${TCN.green}1c`, border: `1px solid ${TCN.green}40`,
                      borderRadius: 999, padding: '4px 9px', fontFamily: TCN.fontMono,
                    }}
                  >
                    +{formatUSD(a.upsideUsd, { fractionDigits: a.upsideUsd < 100 ? 2 : 0 })}
                  </span>
                )}
              </button>
            );
          })}
          {actions.length === 0 && (
            <div style={{ fontSize: 13, color: TCN.textDim, padding: '12px 4px' }}>
              {t('tcn.m5.generate.empty', 'No pay-out gap to act on — every bonus is maxed. 🎉')}
            </div>
          )}
        </div>

        {error && (
          <div style={{ fontSize: 12, color: TCN.red, marginTop: 10 }}>{error}</div>
        )}

        {/* Total upside footer */}
        {actions.length > 0 && (
          <div
            style={{
              marginTop: 16, padding: '14px 16px',
              background: `${TCN.green}12`, border: `1px solid ${TCN.green}33`, borderRadius: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}
          >
            <span style={{ fontSize: 13, color: TCN.text }}>
              {t('tcn.m5.generate.totalIf', 'Total upside if all {{count}} done', { count: selected.length })}
            </span>
            <span dir="ltr" style={{ fontSize: 20, fontWeight: 800, color: TCN.green, fontFamily: TCN.fontMono }}>
              +{formatUSD(totalUpside, { fractionDigits: totalUpside < 100 ? 2 : 0 })}
            </span>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default GenerateActionsSheet;
